/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Thu Jan 11 2024
 **/

import React, { PropsWithChildren, useCallback, useRef, useState } from "react";
import { Box } from "@mui/material";
import styled from "@emotion/styled";

import { getChatTitleByUserList, useChatRoomActions } from "@/frameworks/core/chat";
import { Maybe, Choice } from "@/frameworks/libraries/components";
import { Typography, Row, Icon, Button, TextField, TypographyTooltip } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { EventHandler, useEventHandler } from "@rfice/eventhandler";
import { useMiniChatPopupManager, usePopover } from "@/frameworks/core/hooks";
import { EventID, GROUP_POPOVER_ID, POPOVER_ID } from "@/frameworks/core/commons";
import { UnDraggable } from "@/frameworks/libraries/components/Dragger";

import * as S from "./emotion";
import type { UserInfo } from "./types";

type Props = {
	roomID: string;
	spaceID?: string;
	threadID?: string;
	type: "DM" | "Space";
	background: string;
	userList: UserInfo[];
	privateMode: boolean;
	spaceName?: string;
	roomName?: string;
	title: string;
	subTitle?: string;
	onPointerUp: (e: React.PointerEvent) => void;
	onHide: (e: React.MouseEvent) => void;
	onClose: (e: React.MouseEvent) => void;
};

const ToggleDraggable = ({ children, roomID }: { children: React.ReactNode; roomID: string }) => {
	const popover = usePopover();
	const { isOpen } = popover.usePopoverValue(GROUP_POPOVER_ID.CHAT_OPTION, roomID);

	//@ts-ignore
	if (isOpen) return <UnDraggable>{children}</UnDraggable>;
	return <React.Fragment>{children}</React.Fragment>;
};

export default function Header({
	roomID,
	spaceID,
	threadID,
	type,
	background,
	userList,
	privateMode,
	title,
	subTitle,
	...rest
}: PropsWithChildren<Props>) {
	const popover = usePopover();
	const clickStatus = useRef("end");
	const startClick = () => {
		clickStatus.current = "start";
	};
	const chatRoomActions = useChatRoomActions();
	const [changeRoomID, setChangeRoomID] = useState("");
	const endClick = (callback?: () => void) => {
		if (clickStatus.current === "end") {
			return;
		}
		clickStatus.current = "end";
		callback?.();
	};

	const hide = (ev: React.MouseEvent) => {
		endClick(() => {
			popover.hide(POPOVER_ID.CHAT_EMOTICON_PICKER);
			rest.onHide(ev);
		});
	};

	const close = (ev: React.MouseEvent) => {
		endClick(() => {
			popover.hide(POPOVER_ID.CHAT_EMOTICON_PICKER);
			rest.onClose(ev);
		});
	};
	const [isUpdateChannelName, setIsUpdateChannelName] = useState(false);
	const [channelName, setChannelName] = useState("");

	useEventHandler(EventID.UPDATE_CHANNEL_NAME, (roomID: string) => {
		setChangeRoomID(roomID);
		setIsUpdateChannelName(true);
	});

	const toggleOpenSearchChatInput = useCallback(() => {
		EventHandler.emit(EventID.TOGGLE_OPEN_SEARCH_ON_CONVERSATION_INPUT, { roomId: roomID });
	}, [roomID]);

	const handleChangeChannelName = async () => {
		if (!spaceID) return;
		if (!channelName) {
			setIsUpdateChannelName(false);
			return;
		}
		chatRoomActions.spaceChat.rename({
			spaceId: spaceID,
			roomId: roomID,
			name: channelName,
			events: {
				onSuccess() {
					setIsUpdateChannelName(false);
				}
			}
		});
	};

	const { miniChat } = useMiniChatPopupManager();
	const miniChatValue = miniChat(roomID, spaceID, threadID);

	return (
		<S.Header horizonAlign="distribute" topBarColor={background} type={type} focus={!!miniChatValue?.focus}>
			<Box
				component="div"
				style={{ display: "flex", justifyContent: "space-between", width: " 100%", userSelect: "none" }}
			>
				<S.RoomInfo onPointerUp={rest.onPointerUp}>
					<ToggleDraggable roomID={roomID}>
						<Maybe test={Boolean(subTitle)}>
							<Typography
								typoSize="Body4"
								style={{ maxWidth: "100%" }}
								maxLine={1}
								ellipsis
								typoColor="neutral_70_100"
							>
								{subTitle}
							</Typography>
						</Maybe>
						<Row gap={2} verticalAlign="center" css={{ width: "100%" }}>
							<ChannelUserListWrapper typoSize="Title2" typoColor="neutral_80_100">
								<Choice value={type}>
									<Choice.Case test={"DM"}>
										<TypographyTooltip.p
											tooltipProps={{ hasIcon: false }}
											componentProps={{
												maxLine: 1,
												ellipsis: true,
												style: { marginLeft: "4px" },
												stretch: true,
												typoSize: "Title2"
											}}
										>
											{getChatTitleByUserList(
												userList.map((user) => ({
													id: user.userID,
													name: user.name
												})),
												userList.length
											)}
											{`${userList.length === 1 ? ` ${_T("(나)")}` : ""}`}
										</TypographyTooltip.p>
									</Choice.Case>
									<Choice.Case test={"Space"}>
										<Maybe test={roomID === changeRoomID && isUpdateChannelName}>
											<TextField
												style={{ height: "18px" }}
												autoFocus
												maxLength={50}
												placeholder={title}
												value={channelName || title}
												onClick={(ev) => {
													ev.stopPropagation();
												}}
												onChange={(ev) => {
													setChannelName(ev.target.value);
												}}
												onKeyDown={(ev) => {
													if (ev.key === "Enter") {
														handleChangeChannelName();
													}
												}}
												onBlur={() => {
													handleChangeChannelName();
												}}
											/>
											<Maybe.Fallback>{title}</Maybe.Fallback>
										</Maybe>
									</Choice.Case>
								</Choice>
							</ChannelUserListWrapper>
							<Icon name="Down24" size="16" />
						</Row>
						<Row>
							<Maybe test={type === "DM" || privateMode}>
								{userList.length > 2 ? (
									<Typography typoSize="Body4" typoColor="neutral_70_100">
										{_T("채팅멤버 {{count}}명", {
											count: userList.length
										})}
									</Typography>
								) : null}
							</Maybe>
						</Row>
					</ToggleDraggable>
				</S.RoomInfo>
				<Row gap={0} verticalAlign="top">
					<Button.Icon
						shape="round"
						name="Search24"
						size="medium"
						variant="text"
						onClick={toggleOpenSearchChatInput}
					/>
					<Button.Icon
						shape="round"
						name="Minimize24"
						size="medium"
						variant="text"
						onMouseDown={startClick}
						onMouseUp={hide}
					/>
					<Button.Icon
						shape="round"
						name="Close24"
						size="medium"
						variant="text"
						onMouseDown={startClick}
						onMouseUp={close}
					/>
				</Row>
			</Box>
		</S.Header>
	);
}

const ChannelUserListWrapper = styled(Typography)`
	max-width: calc(100% - 20px);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	word-break: break-all;
`;
